import { LegalNameHover } from "@/components/conversations/legal-name-hover";
import {
  Badge,
  ChatHeader,
  IconButton,
  PresenceAvatar,
  type BadgeVariant,
} from "@somethings-ui/web";
import {
  CalendarClock,
  Flag,
  MoreVertical,
  PhoneCall,
  TriangleAlert,
} from "lucide-react";
import type { ReactNode } from "react";

export type AttentionHeaderBadgeKind = "reactivated" | "rematched";

export interface AttentionHeaderBadge {
  kind: AttentionHeaderBadgeKind;
  label: string;
}

export type AttentionHeaderActionId =
  | "call-teen"
  | "schedule-call"
  | "escalate"
  | "report";

export interface AttentionHeaderAction {
  id: AttentionHeaderActionId;
  label: string;
  onSelect: () => void;
  disabled?: boolean;
  destructive?: boolean;
}

export interface AttentionHeaderProps {
  name: string;
  legalName?: string;
  avatarUrl?: string | null;
  online: boolean;
  subtitleParts: string[];
  badges: AttentionHeaderBadge[];
  actions: AttentionHeaderAction[];
}

const BADGE_VARIANT: Record<AttentionHeaderBadgeKind, BadgeVariant> = {
  reactivated: "brand",
  rematched: "neutral",
};

const ACTION_ICON: Record<AttentionHeaderActionId, ReactNode> = {
  "call-teen": <PhoneCall className="size-4" />,
  "schedule-call": <CalendarClock className="size-4" />,
  escalate: <TriangleAlert className="size-4" />,
  report: <Flag className="size-4" />,
};

// Rendered as disabled "coming soon" items until their flows are built.
const COMING_SOON_ACTIONS: ReadonlySet<AttentionHeaderActionId> = new Set([
  "report",
]);

export function AttentionHeader({
  name,
  legalName,
  avatarUrl,
  online,
  subtitleParts,
  badges,
  actions,
}: AttentionHeaderProps) {
  return (
    <ChatHeader>
      <ChatHeader.Leading>
        <PresenceAvatar
          name={name}
          imageUrl={avatarUrl ?? undefined}
          online={online}
          size="md"
        />
      </ChatHeader.Leading>

      <ChatHeader.Content>
        <ChatHeader.Title>
          <LegalNameHover preferredName={name} legalName={legalName}>
            <ChatHeader.Name>{name}</ChatHeader.Name>
          </LegalNameHover>
          {badges.map((badge) => (
            <Badge key={badge.kind} variant={BADGE_VARIANT[badge.kind]}>
              {badge.label}
            </Badge>
          ))}
        </ChatHeader.Title>
        {subtitleParts.length > 0 ? (
          <ChatHeader.Subtitle items={subtitleParts} />
        ) : null}
      </ChatHeader.Content>

      {actions.length > 0 ? (
        <ChatHeader.Actions>
          <ChatHeader.ActionsMenu>
            <ChatHeader.ActionsMenu.Trigger>
              <IconButton
                variant="tertiary"
                size="md"
                shape="circle"
                aria-label="Conversation actions"
                icon={<MoreVertical className="size-5" />}
              />
            </ChatHeader.ActionsMenu.Trigger>
            <ChatHeader.ActionsMenu.Content className="w-60">
              {actions.map((action) => {
                const comingSoon = COMING_SOON_ACTIONS.has(action.id);
                return (
                  <ChatHeader.ActionsMenu.Item
                    key={action.id}
                    icon={ACTION_ICON[action.id]}
                    disabled={comingSoon || action.disabled}
                    onSelect={action.onSelect}
                    className={
                      !comingSoon && action.destructive
                        ? "text-states-error"
                        : undefined
                    }
                  >
                    {action.label}
                    {comingSoon ? (
                      <span className="ml-1 text-text-tertiary">
                        · Coming soon
                      </span>
                    ) : null}
                  </ChatHeader.ActionsMenu.Item>
                );
              })}
            </ChatHeader.ActionsMenu.Content>
          </ChatHeader.ActionsMenu>
        </ChatHeader.Actions>
      ) : null}
    </ChatHeader>
  );
}
